import React, { useState } from 'react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail
} from "firebase/auth";
import './Auth.css';

function Auth({ onClose }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (!auth) {
      setError("Firebase Auth not initialized correctly.");
      return;
    }
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (!methods || methods.length === 0) {
        setError("Couldn't find an account with that email address.");
        return;
      }
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        setError("Please enter a valid email address.");
      } else if (err.code === 'auth/user-not-found') {
        setError("Couldn't find an account with that email address.");
      } else {
        setError(err.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!auth) {
      setError("Firebase Auth not initialized correctly.");
      return;
    }

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onClose();
    } catch (err) {
      console.error("Auth Error Code:", err.code);
      console.error("Auth Error Message:", err.message);
      
      let friendlyMessage = err.message;
      if (err.code === 'auth/operation-not-allowed') {
        friendlyMessage = "Email/Password sign-in is not enabled in your Firebase Console. Please enable it under Authentication > Sign-in method.";
      } else if (err.code === 'auth/invalid-credential') {
        friendlyMessage = isSignUp 
          ? "Invalid registration details or configuration. Check your Firebase settings."
          : "Invalid email or password.";
      } else if (err.code === 'auth/email-already-in-use') {
        friendlyMessage = "This email is already registered. Try signing in instead.";
      } else if (err.code === 'auth/weak-password') {
        friendlyMessage = "The password is too weak. Please use at least 6 characters.";
      }
      
      setError(friendlyMessage);
    }
  };

  const signInWithGoogle = async () => {
    if (!auth) {
      setError("Firebase Auth not initialized correctly.");
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged in App.js fires automatically — just close the modal
      onClose();
    } catch (err) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        // Popup was blocked by browser — fall back to redirect flow
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr) {
          setError(redirectErr.message);
        }
      } else {
        setError(err.message);
      }
    }
  };

  if (isForgot) {
    return (
      <div className="Auth-modal">
        <div className="Auth-content">
          <button className="Close-button" onClick={onClose}>&times;</button>
          <h2>Reset Password</h2>

          {error && <div className="Auth-error">{error}</div>}

          {resetSent ? (
            <div className="Auth-success">
              Check your inbox — a password reset link has been sent to <strong>{email}</strong>.
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <p className="Auth-hint">Enter your email and we'll send you a reset link.</p>
              <div className="Form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="Submit-button">Send Reset Link</button>
            </form>
          )}

          <p className="Toggle-auth">
            <button onClick={() => { setIsForgot(false); setResetSent(false); setError(null); }}>
              Back to Sign In
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="Auth-modal">
      <div className="Auth-content">
        <button className="Close-button" onClick={onClose}>&times;</button>
        <h2>{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>

        {error && <div className="Auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="Form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="Form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {!isSignUp && (
            <div className="Forgot-link">
              <button type="button" onClick={() => { setIsForgot(true); setError(null); }}>
                Forgot password?
              </button>
            </div>
          )}
          <button type="submit" className="Submit-button">
            {isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        <div className="Divider">
          <span>OR</span>
        </div>

        <button className="Google-button" onClick={signInWithGoogle}>
          Continue with Google
        </button>

        <p className="Toggle-auth">
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <button onClick={() => setIsSignUp(!isSignUp)}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </p>
      </div>
    </div>
  );
}

export default Auth;
